/**
 * Present Case history events from the A8 timeline API.
 * Explicit nouns only. No causal wording. No invented timestamps.
 */

import { coerceRegisterRevision } from './workspaceRegisters';
import { safetyScanVerifyPath } from '../acceptance/scanVerifyNavigation';

export type CaseHistoryEventView = {
  eventId: string;
  eventType: string;
  title: string;
  kind: string;
  occurredAt: string | null;
  summary: string;
  detailLines: string[];
  href: string | null;
  linkLabel: string | null;
  scanCreated: boolean | null;
  noScanReason: string | null;
};

const CAUSAL = /\b(caused|prevented|reduced risk)\b/i;

function asText(value: unknown): string {
  return String(value ?? '').trim();
}

function related(event: Record<string, unknown>): Record<string, unknown> {
  const blob = event.related;
  return blob && typeof blob === 'object' && !Array.isArray(blob)
    ? blob as Record<string, unknown>
    : {};
}

export function presentCaseHistoryEvent(event: Record<string, unknown>): CaseHistoryEventView | null {
  const title = asText(event.title);
  const eventId = asText(event.event_id);
  if (!title || !eventId) return null;
  const extra = related(event);
  const summary = asText(event.summary);
  const lines: string[] = [];
  const windowStart = asText(extra.window_start_utc);
  const windowEnd = asText(extra.window_end_utc);
  if (windowStart && windowEnd && title !== 'Control Evaluation') {
    lines.push(`${windowStart} – ${windowEnd}`);
  }
  if (title === 'Scheduled Run' && extra.scan_created === false && asText(extra.no_scan_reason)) {
    const reason = asText(extra.no_scan_reason);
    if (reason && !summary.includes(reason)) lines.push(reason);
  }
  if (title === 'Signed Record' && asText(extra.signer_name)) {
    const signedBy = `Signed by ${asText(extra.signer_name)}`;
    if (!summary.includes(signedBy)) lines.push(signedBy);
  }
  if (title === 'Control Evaluation') {
    const before = asText(extra.before_report_id);
    const after = asText(extra.after_report_id);
    if (before && after) lines.push(`Before ${before} · After ${after}`);
    const label = asText(extra.customer_result_label);
    if (label) lines.push(label);
  }
  const reportId = asText(extra.report_id);
  let href: string | null = null;
  let linkLabel: string | null = null;
  if (title === 'Safety Scan' && reportId) {
    href = safetyScanVerifyPath(reportId);
    linkLabel = 'Open Safety Scan';
  } else if (title === 'Signed Record' && reportId) {
    const revision = coerceRegisterRevision({
      revision: typeof extra.revision === 'number' || typeof extra.revision === 'string'
        ? extra.revision
        : (typeof extra.current_revision === 'number' || typeof extra.current_revision === 'string'
          ? extra.current_revision
          : null),
      revisions: Array.isArray(extra.revisions)
        ? extra.revisions as Array<{ revision?: number | null }>
        : null,
    });
    href = revision != null
      ? `/acceptance/reports/records/${encodeURIComponent(reportId)}/revisions/${revision}`
      : null;
    linkLabel = href ? 'Open Signed Record' : null;
  } else if (title === 'Control' || title === 'Control Evaluation') {
    href = extra.case_id ? `/acceptance/cases/${encodeURIComponent(asText(extra.case_id))}?tab=controls` : null;
    linkLabel = title === 'Control Evaluation' ? 'Open Evaluation' : 'Open Control';
  } else if (title === 'Scheduled Run' && extra.scan_created === false) {
    href = null;
    linkLabel = null;
  }
  const scanCreated = extra.scan_created === true ? true : extra.scan_created === false ? false : null;
  return {
    eventId,
    eventType: asText(event.event_type),
    title,
    kind: asText(event.kind),
    occurredAt: asText(event.occurred_at) || null,
    summary: CAUSAL.test(summary) ? summary.replace(CAUSAL, '') : summary,
    detailLines: lines,
    href,
    linkLabel,
    scanCreated,
    noScanReason: asText(extra.no_scan_reason) || null,
  };
}

export function presentCaseHistory(events: unknown): CaseHistoryEventView[] {
  if (!Array.isArray(events)) return [];
  return events
    .map((item) => presentCaseHistoryEvent((item && typeof item === 'object') ? item as Record<string, unknown> : {}))
    .filter((item): item is CaseHistoryEventView => Boolean(item));
}

const SUMMARY_LIMIT = 6;

export function presentCaseHistoryForDisplay(
  events: unknown,
  density: 'summary' | 'full' = 'full',
): { rows: CaseHistoryEventView[]; collapsedRunCount: number } {
  const all = presentCaseHistory(events);
  const kept: CaseHistoryEventView[] = [];
  let collapsedRunCount = 0;
  let technicalRunStreak = 0;
  for (const row of all) {
    const technicalRun = row.eventType === 'scheduled_run' && row.scanCreated === true;
    if (technicalRun) {
      technicalRunStreak += 1;
      if (technicalRunStreak === 1) {
        kept.push(row);
      } else {
        collapsedRunCount += 1;
      }
      continue;
    }
    technicalRunStreak = 0;
    kept.push(row);
  }
  if (density === 'summary') {
    return { rows: kept.slice(0, SUMMARY_LIMIT), collapsedRunCount };
  }
  return { rows: kept, collapsedRunCount };
}
